import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { GapOfferResult, JoinQueue, OfferGapToQueue, QueueTicket } from '@salonomy/types';
import { api, ApiClientError, unwrap } from '../lib/api.js';
import { useOffline } from '../lib/offline/provider.js';
import { qk } from '../lib/query.js';
import { useEventSource } from '../lib/sse.js';
import { useToast } from '../lib/toast.js';

/** Mirrors the API's GET /v1/queue response (the SSE `queue` event carries the same, sans avg). */
export interface QueueState {
  open: boolean;
  waitingCount: number;
  avgWaitMin: number | null;
  tickets: QueueTicket[];
}

export type QueueActionKind = 'call' | 'hold' | 'no_show' | 'complete' | 'requeue';

function deriveAvgWait(tickets: QueueTicket[]): number | null {
  const now = Date.now();
  const waits = tickets
    .filter((t) => t.etaAt && (t.status === 'waiting' || t.status === 'called'))
    .map((t) => Math.max(0, Math.round((new Date(t.etaAt!).getTime() - now) / 60000)));
  return waits.length ? Math.round(waits.reduce((a, b) => a + b, 0) / waits.length) : null;
}

export function useQueue() {
  return useQuery({
    queryKey: qk.queue,
    queryFn: async (): Promise<QueueState> => unwrap(await api.GET('/v1/queue')),
  });
}

/**
 * Open one staff SSE connection for the whole authed session and fold every push into the query
 * cache (ADR-0004). Call this once near the top of the tree; components read via useQueue().
 */
export function useQueueLiveSubscription(enabled: boolean) {
  const qc = useQueryClient();
  return useEventSource(
    '/v1/queue/stream',
    {
      queue: (data) => {
        const payload = data as { open: boolean; waitingCount: number; tickets: QueueTicket[] };
        qc.setQueryData<QueueState>(qk.queue, {
          open: payload.open,
          waitingCount: payload.waitingCount,
          avgWaitMin: deriveAvgWait(payload.tickets),
          tickets: payload.tickets,
        });
      },
    },
    enabled,
  );
}

export function useQueueAction() {
  const qc = useQueryClient();
  const { enqueue } = useOffline();
  const toast = useToast();
  return useMutation<
    QueueTicket | null,
    unknown,
    { id: string; action: QueueActionKind },
    { prev?: QueueState }
  >({
    mutationFn: async ({ id, action }) => {
      try {
        return unwrap(
          await api.POST('/v1/queue/{id}/action', {
            params: { path: { id } },
            body: { action },
          }),
        );
      } catch (err) {
        if (!(err instanceof ApiClientError)) {
          await enqueue('queue.action', { id, action }, [qk.queue]);
          return null;
        }
        throw err;
      }
    },
    onMutate: async ({ id, action }) => {
      await qc.cancelQueries({ queryKey: qk.queue });
      const prev = qc.getQueryData<QueueState>(qk.queue);
      if (prev) {
        const nextStatus =
          action === 'call'
            ? ('called' as const)
            : action === 'complete'
              ? ('completed' as const)
              : action === 'no_show'
                ? ('no_show' as const)
                : ('waiting' as const);
        qc.setQueryData<QueueState>(qk.queue, {
          ...prev,
          tickets: prev.tickets.map((t) => (t.id === id ? { ...t, status: nextStatus } : t)),
        });
      }
      return { prev };
    },
    onError: (err, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.queue, ctx.prev);
      toast.error(err instanceof ApiClientError ? err.message : 'Could not update the ticket');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: qk.queue }),
  });
}

export function useJoinQueue() {
  const qc = useQueryClient();
  const { enqueue } = useOffline();
  const toast = useToast();
  return useMutation<QueueTicket | null, unknown, JoinQueue>({
    mutationFn: async (body) => {
      try {
        return unwrap(await api.POST('/v1/queue/join', { body }));
      } catch (err) {
        if (!(err instanceof ApiClientError)) {
          await enqueue('queue.join', body, [qk.queue]);
          return null;
        }
        throw err;
      }
    },
    onError: (err) =>
      toast.error(err instanceof ApiClientError ? err.message : 'Could not add to the queue'),
    onSettled: () => qc.invalidateQueries({ queryKey: qk.queue }),
  });
}

export function useToggleQueue() {
  const qc = useQueryClient();
  const toast = useToast();
  return useMutation<{ open: boolean }, unknown, boolean, { prev?: QueueState }>({
    mutationFn: async (open) => unwrap(await api.PUT('/v1/queue/open', { body: { open } })),
    onMutate: async (open) => {
      await qc.cancelQueries({ queryKey: qk.queue });
      const prev = qc.getQueryData<QueueState>(qk.queue);
      if (prev) qc.setQueryData<QueueState>(qk.queue, { ...prev, open });
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(qk.queue, ctx.prev);
      toast.error('Could not change the queue');
    },
    onSettled: () => qc.invalidateQueries({ queryKey: qk.queue }),
  });
}

export function useOfferGap() {
  return useMutation<GapOfferResult, unknown, OfferGapToQueue>({
    mutationFn: async (body) => unwrap(await api.POST('/v1/queue/offer-gap', { body })),
  });
}
